// bm-design-system: finding card primitive
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const findingCardVariants = cva("rounded-xl border border-hairline border-l-4 p-5 sm:p-6", {
  variants: {
    status: {
      fail: "border-l-danger bg-danger-faded/40",
      warn: "border-l-signal bg-signal-faded/40",
      pass: "border-l-success bg-success-faded/40",
    },
  },
  defaultVariants: {
    status: "pass",
  },
});

const STATUS_ICON = { fail: XCircle, warn: AlertTriangle, pass: CheckCircle2 } as const;

const STATUS_ICON_CLASS = {
  fail: "text-danger-display",
  warn: "text-signal-display",
  pass: "text-success-display",
} as const;

export interface FindingCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof findingCardVariants> {
  title: React.ReactNode;
  body: React.ReactNode;
  detail?: React.ReactNode;
}

const FindingCard = React.forwardRef<HTMLDivElement, FindingCardProps>(
  ({ className, status, title, body, detail, ...props }, ref) => {
    const resolvedStatus = status ?? "pass";
    const Icon = STATUS_ICON[resolvedStatus];

    return (
      <div ref={ref} className={cn(findingCardVariants({ status, className }))} {...props}>
        <div className="flex items-start gap-3">
          <Icon
            className={cn("mt-0.5 h-5 w-5 shrink-0", STATUS_ICON_CLASS[resolvedStatus])}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <h3>{title}</h3>
            <p className="mt-1 text-ink-body">{body}</p>
            {detail && <p className="mt-2 text-sm text-ink-muted">{detail}</p>}
          </div>
        </div>
      </div>
    );
  },
);
FindingCard.displayName = "FindingCard";

export { FindingCard, findingCardVariants };
